import { Box, Flex, Icon, Image, Text, useDisclosure } from '@chakra-ui/react';
import { useContext, useMemo } from 'react';
import { AiFillCheckCircle } from 'react-icons/ai';

import { toaster } from '~/components/ui/toaster';
import { useI18nLn } from '~/hooks/store';
import { apiClient } from '~/lib/apiClient';
import { RootContext } from '~/store';
import { GmoCredit } from '~/types/resources/gmo_credit';
import { DeleteCreditModal } from './DeleteCreditModal';

type Props = {
  credit: GmoCredit;
  isChecked: boolean;
  setSelectedCardSeq: (card_seq: string | null) => void;
  setValue: (card_seq: string | null) => void;
  mutate: () => void;
};

export const UserCreditSelectCard: React.FC<Props> = (props: Props) => {
  const { credit } = props;
  const { token } = useContext(RootContext);
  const { t } = useI18nLn();
  const creditDisclosure = useDisclosure();

  const expire_string: string = useMemo(() => {
    const str = props.credit.expire;
    const year_and_month = str.match(/.{2}/g);
    if (year_and_month === null) return '';
    return `${year_and_month[0]}/${year_and_month[1]}`;
  }, [props.credit]);

  const handleDelete = async () => {
    await apiClient.v1.me.credits._cardSeqId(credit.cardSeq).$delete({
      headers: {
        Authorization: `Bearer ${token?.token}`,
      },
    });
    props.setValue(null);
    props.mutate();
    toaster.success({
      title: '指定のカードを削除しました',
      duration: 2000,
    });
  };

  return (
    <>
      <Flex as="label" mb="16px">
        <Flex
          cursor="pointer"
          border="2px"
          borderColor={props.isChecked ? 'primary' : 'disable'}
          borderStyle="solid"
          borderRadius="10px"
          py="16px"
          flexDir="row"
          alignItems="center"
          justifyContent="center"
          w="full"
          onClick={() => {
            props.setValue(credit.cardSeq);
            props.setSelectedCardSeq(credit.cardSeq);
          }}
        >
          <Box w="12px" />
          <Flex
            h="16px"
            w="16px"
            rounded="full"
            justifyContent="center"
            alignItems="center"
          >
            <Icon
              as={AiFillCheckCircle}
              color={props.isChecked ? 'primary' : 'disable'}
            />
          </Flex>
          <Box w="16px" />
          <Flex flexDir="column" justify="left" flex={1}>
            <Text fontSize="12px" lineHeight="16px" color="desc">
              {t('pages.buy.payments.credit.cardno')}
            </Text>
            <Box h="8px" />
            <Text fontSize="15px" lineHeight="20px" letterSpacing="2px">
              {credit.cardNo}
            </Text>
          </Flex>
          <Box w="8px" />
          <Flex flexDir="column" justify="left" flex={1}>
            <Text fontSize="12px" lineHeight="16px" color="desc">
              {t('pages.buy.payments.credit.exp')}
            </Text>
            <Box h="8px" />
            <Text fontSize="15px" lineHeight="20px" letterSpacing="2px">
              {expire_string}
            </Text>
          </Flex>
          <Box w="16px" />
          <Box
            h="24px"
            w="24px"
            p="4px"
            bgColor="white"
            backgroundColor="#1fe0"
            onClick={creditDisclosure.onOpen}
          >
            <Image
              src="/assets/icon/delete.svg"
              alt="icon"
              h="16px"
              w="16px"
              color="disable"
              m={0}
              p={0}
            />
          </Box>
          <Box w="12px" />
        </Flex>
      </Flex>
      <DeleteCreditModal
        credit={props.credit}
        isOpen={creditDisclosure.open}
        onClose={creditDisclosure.onClose}
        handleDelete={handleDelete}
      />
    </>
  );
};
